import React from 'react';

import type { ProviderContractType } from '@travel/traveler-core/constants';
import { useDeviceTypes } from '@travel/traveler-core/hooks';
import usePriceLabel from '@travel/traveler-core/hooks/usePriceLabel';
import { IconTextList } from '@travel/ui';
import { isEmptyObject } from '@travel/utils';

import Media from 'components/Media';
import Money from 'components/Money';

import { Translate } from 'core/translate';
import type { SearchFormQuery } from 'ProviderList-Types';
import type { RatePlanItem } from 'ProviderRatePlan-Types';

import PriceLabel from '../../../components/PriceLabel';
import RoomBasicInfoSummary from '../../../pages/ProviderInformationPage/components/RoomBasicInfoSummary';
import { mappedIconTextListData } from '../../ProviderInformationPage/helpers';

import styles from './roomItemCard.module.scss';

type Props = {
  ratePlanItem: RatePlanItem;
  query: { itemRatePlanId: string; contractType: ProviderContractType };
  isDirectContract: boolean;
};

const CardTitle = ({ name }: { name: string }) => (
  <h3 className={styles.cardTitle} data-testid="roomItemCard-cardTitle">
    {name}
  </h3>
);

export const RoomItemCard = ({ ratePlanItem, query, isDirectContract }: Props) => {
  const { name, media, plans, smokingAllowed, view, facilities } = ratePlanItem;
  const featureIcons = mappedIconTextListData(facilities?.[0]?.features, styles.iconSize);
  const { guestLabel, nightLabel, roomsLabelText } = usePriceLabel(
    query as SearchFormQuery,
  );

  const { isSP } = useDeviceTypes();

  return (
    <div className={styles.roomCard}>
      <div className={styles.roomCardImageWrapper}>
        <Media
          media={media[0]}
          className={styles.roomCardImageWrapper}
          data-testid="roomItemCard-itemImage"
        />
      </div>

      <div className={styles.cardContent}>
        {!isSP && <CardTitle name={name} />}

        <div className={styles.featurePriceContainer}>
          <div data-testid="roomItemCard-basicInfo">
            <div className={styles.roomBasicInfoContainer}>
              {isSP && <CardTitle name={name} />}
              <RoomBasicInfoSummary
                smokingAllowed={smokingAllowed}
                viewText={!isEmptyObject(view) ? view.text : ''}
                customStyle="iconText"
              />
            </div>

            <div className={styles.IconTextListContainer}>
              <IconTextList
                iconTextSpacing={4}
                spaceBetween={16}
                items={featureIcons.slice(0, 3)}
                data-testid="guideUserPage-availableRoomItem-features"
              />
            </div>
          </div>

          <div className={styles.roomCardPricing}>
            <PriceLabel
              isPromotionLabelHidden={false}
              price={plans[0]?.price}
              unitPriceClassName={styles.unitPrice}
              planPaymentRestrictionFeature={plans[0]?.paymentRestrictionFeature}
              PriceComponent={(props: { value: number }) => <Money {...props} roundPosition={-1} />}
              chargeUnit={
                <Translate id="Provider_List.Out_To_Out.Search_Results.Per_Room_Per_Night" />
              }
              roomsLabel={roomsLabelText}
              guestsLabel={guestLabel}
              nightsLabel={nightLabel}
              taxesLabel={
                <Translate
                  id={
                    plans[0]?.price?.isConsumptionTaxIncluded
                      ? 'Provider_List.Search_Results.Taxes'
                      : 'Provider_List.Luxury.Tax_Excluded'
                  }
                  className={styles.providerInfoLabel}
                />
              }
              isDirectContract={isDirectContract}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
